import { DOCUMENT } from '@angular/common';
import { AfterContentInit, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreTranslationService } from '@core/services/translation.service';
import { TranslateService } from '@ngx-translate/core';
import { locale as menuEnglish } from 'app/menu/i18n/en';
import { locale as menuPortuguese } from 'app/menu/i18n/pt';
import { getMenu } from 'app/menu/menu';
import { filterMenuByTipoSistema } from 'app/menu/menu-filter';
import { AllowIn, ShortcutInput } from 'ng-keyboard-shortcuts';
import { CookieService } from 'ngx-cookie-service';
import * as Waves from 'node-waves';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppService } from './app.service';
import { Usuario } from './interfaces/usuario';
import { UsuarioTempLogin } from './interfaces/usuario-temp-login';
import { SearchService } from './layout/components/navbar/navbar-search/search.service';
import { AuthService } from './main/pages/authentication/auth.service';
import { ThemeService } from './theme.service';
import { AtividadesAtrasadasService } from './utils/atividades-atrasadas.service';
import { avisoDataDigitada$ } from './utils/flatpickr-data-estrita';
import { MessageService } from './utils/toastr-custom/message.service';
import { ParametroUtilsService } from './utils/parametro-utils.service';
import { SessionStorageService } from './utils/session-storage.service';
import { VersaoService } from './utils/versao.service';
import { getTipoSistemaKey, TipoSistemaKey } from './interfaces/enum/tipo-sistema';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterContentInit, OnDestroy {
  coreConfig: any;
  menu: any;
  defaultLanguage: 'en'; // This language will be used as a fallback when a translation isn't found in the current language
  appLanguage: 'en'; // Set application default language i.e fr
  public usuarioTemporario: UsuarioTempLogin;
  public empresaBackground = 'smx';

  // Private
  private _unsubscribeAll: Subject<any>;

  usuarioLogado: Usuario;

  shortcuts: ShortcutInput[] = [];

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _title: Title,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    public _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _coreMenuService: CoreMenuService,
    private _coreTranslationService: CoreTranslationService,
    private _parametrosUtilsService: ParametroUtilsService,
    private _translateService: TranslateService,
    public appService: AppService,
    private __sessionStorageService: SessionStorageService,
    private _authService: AuthService,
    private _appService: AppService,
    private _cookieService: CookieService,
    private _theme: ThemeService,
    private _searchService: SearchService,
    private _router: Router,
    private _atividadeAtrasadasService: AtividadesAtrasadasService,
    private _messageService: MessageService,
    private _versaoService: VersaoService
  ) {
    this.menu = filterMenuByTipoSistema(getMenu(), null);

    // Register the menu to the menu service
    this._coreMenuService.register('main', this.menu);

    // Set the main menu as our current menu
    this._coreMenuService.setCurrentMenu('main');

    // Add languages to the translation service
    this._translateService.addLangs(['pt', 'en']);

    // This language will be used as a fallback when a translation isn't found in the current language
    this._translateService.setDefaultLang('pt');

    // Set the translations for the menu
    this._coreTranslationService.translate(menuEnglish, menuPortuguese);

    // Set the private defaults
    this._unsubscribeAll = new Subject();

    try {
      this.usuarioLogado = JSON.parse(__sessionStorageService.getItem('user'));
    } catch (error) {
      sessionStorage.clear();
    }

    window.addEventListener('load', () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('/service-worker.js').then(registration => {
          registration.update();
        }).catch(error => {
          console.warn('ServiceWorker registration failed:', error);
        });
      }
    });
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngAfterContentInit(): void { }

  ngAfterViewInit(): void {
    this.shortcuts.push({
      key: 'ctrl + space',
      label: 'Pesquisar',
      description: 'Pesquisar no sistema.',
      allowIn: [AllowIn.Textarea, AllowIn.Input],
      command: () => this._searchService.openSearchBar(),
      preventDefault: true,
    });
  }

  get progress() {
    return this._appService.progressSpinnerDlg;
  }

  /**
   * On init
   */
  ngOnInit(): void {
    this.appService.setUsuarioSubject(this.usuarioLogado);
    this._authService.getUserPermissions();

    if (this.usuarioLogado && !this.isRotaDeLogin(this._router.url)) {
      this.tentaAtualizarMenuPorTipoSistema();
    }

    // Init wave effect (Ripple effect)
    Waves.init();

    this._versaoService.iniciaVerificacao();

    avisoDataDigitada$.pipe(takeUntil(this._unsubscribeAll)).subscribe(aviso => {
      this._messageService.warning(aviso.titulo, aviso.mensagem);
    });

    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;

      const appLanguage = this.coreConfig.app.appLanguage || 'pt';
      this._translateService.use(appLanguage);

      setTimeout(() => {
        this._translateService.setDefaultLang('pt');
        this._translateService.setDefaultLang(appLanguage);
      });

      this._elementRef.nativeElement.classList.remove('vertical-layout', 'vertical-menu-modern', 'horizontal-layout', 'horizontal-menu');
      // Add class based on config options
      if (this.coreConfig.layout.type === 'vertical') {
        this._elementRef.nativeElement.classList.add('vertical-layout', 'vertical-menu-modern');
      } else if (this.coreConfig.layout.type === 'horizontal') {
        this._elementRef.nativeElement.classList.add('horizontal-layout', 'horizontal-menu');
      }

      // Navbar
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove('navbar-floating', 'navbar-static', 'navbar-sticky', 'navbar-hidden');

      // Add class based on config options
      if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
        this._elementRef.nativeElement.classList.add('navbar-static');
      } else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
        this._elementRef.nativeElement.classList.add('navbar-sticky');
      } else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
        this._elementRef.nativeElement.classList.add('navbar-floating');
      } else {
        this._elementRef.nativeElement.classList.add('navbar-hidden');
      }

      // Footer
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove('footer-fixed', 'footer-static', 'footer-hidden');

      // Add class based on config options
      if (this.coreConfig.layout.footer.type === 'footer-sticky') {
        this._elementRef.nativeElement.classList.add('footer-fixed');
      } else if (this.coreConfig.layout.footer.type === 'footer-static') {
        this._elementRef.nativeElement.classList.add('footer-static');
      } else {
        this._elementRef.nativeElement.classList.add('footer-hidden');
      }

      // Blank layout
      if (this.coreConfig.layout.menu.hidden && this.coreConfig.layout.navbar.hidden && this.coreConfig.layout.footer.hidden) {
        this._elementRef.nativeElement.classList.add('blank-page');
        // ! Fix: Transition issue while coming from blank page
        this._renderer.setAttribute(this._elementRef.nativeElement.getElementsByClassName('app-content')[0], 'style', 'transition:none');
      } else {
        this._elementRef.nativeElement.classList.remove('blank-page');
        // ! Fix: Transition issue while coming from blank page
        setTimeout(() => {
          this._renderer.setAttribute(this._elementRef.nativeElement.getElementsByClassName('app-content')[0], 'style', 'transition:300ms ease all');
        }, 0);
        // If navbar hidden
        if (this.coreConfig.layout.navbar.hidden) {
          this._elementRef.nativeElement.classList.add('navbar-hidden');
        }
        // Menu (Vertical menu hidden)
        if (this.coreConfig.layout.menu.hidden) {
          this._renderer.setAttribute(this._elementRef.nativeElement, 'data-col', '1-column');
        } else {
          this._renderer.removeAttribute(this._elementRef.nativeElement, 'data-col');
        }
        // Footer
        if (this.coreConfig.layout.footer.hidden) {
          this._elementRef.nativeElement.classList.add('footer-hidden');
        }
      }

      // Skin Class (Adding to body as it requires highest priority)
      if (this.coreConfig.layout.skin !== '' && this.coreConfig.layout.skin !== undefined) {
        this.document.body.classList.remove('default-layout', 'bordered-layout', 'dark-layout', 'semi-dark-layout');
        this.document.body.classList.add(this.coreConfig.layout.skin + '-layout');
      }

    });
    this._title.setTitle(this.coreConfig.app.appTitle);
    this.verificaMudancasDeRotas();
  }

  verificaMudancasDeRotas(): void {
    this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects || event.url;

        if (this.isRotaDeLogin(url)) {
          this.registraMenu(filterMenuByTipoSistema(getMenu(), null));
          return;
        }

        this.tentaAtualizarMenuPorTipoSistema();

        if (
          url !== '/todo' &&
          url !== '/404' &&
          url !== '/auth/error' &&
          url !== '/auth/forgot-password'
        ) {
          this._atividadeAtrasadasService.verificaAtividadesAtrasadas();
        }
      }
    });
  }

  private tentaAtualizarMenuPorTipoSistema(tentativa: number = 1): void {
    if (this.isRotaDeLogin(this._router.url)) {
      return;
    }

    const menuAtualizado = this.atualizaMenuPorTipoSistema();

    if (!menuAtualizado && tentativa < 20) {
      setTimeout(() => {
        this.tentaAtualizarMenuPorTipoSistema(tentativa + 1);
      }, 300);
    }
  }

  private atualizaMenuPorTipoSistema(): boolean {
    try {
      const parametro = this._parametrosUtilsService.getParametroByNome('PARAM_TIPO_SISTEMA');
      const tipoSistemaKeys = this.getTipoSistemaKeysFromParametro(parametro?.valor);

      if (!tipoSistemaKeys.length) {
        this.registraMenu(filterMenuByTipoSistema(getMenu(), null));
        return false;
      }

      this.registraMenu(filterMenuByTipoSistema(getMenu(), tipoSistemaKeys));
      return true;
    } catch (error) {
      console.warn('Erro ao filtrar menu por tipo de sistema:', error);

      this.registraMenu(filterMenuByTipoSistema(getMenu(), null));
      return false;
    }
  }

  private registraMenu(menu: any): void {
    this.menu = menu;

    this._coreMenuService.unregister('main');
    this._coreMenuService.register('main', this.menu);
    this._coreMenuService.setCurrentMenu('main');
  }

  private getTipoSistemaKeysFromParametro(valor: any): TipoSistemaKey[] {
    if (!valor) {
      return [];
    }

    const valores = Array.isArray(valor) ? valor : [valor];

    return valores
      .map(item => getTipoSistemaKey(item))
      .filter((tipoSistemaKey): tipoSistemaKey is TipoSistemaKey => !!tipoSistemaKey);
  }

  private isRotaDeLogin(url: string): boolean {
    return (
      url.includes('/auth/login') ||
      url.includes('/login') ||
      url.includes('/auth/error') ||
      url.includes('/auth/forgot-password')
    );
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Busca se ja existe informações do usuário no cookie;
   */
  verificaUsuarioTemporario() {
    try {
      this.usuarioTemporario = JSON.parse(this._cookieService.get('USUARIO_TEMP'));

      if (this.usuarioTemporario) {
        this._theme.current = this._appService.getEmpresaBackground(this.usuarioTemporario.empresa);
      }
    } catch (error) { }
  }
}